import PageHeader from "../../shared-components/PageHeader";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../../shared-components/Button";
import Container from "../../shared-components/Container";
import { useState } from "react";
import UploadDropZone from "./UploadDropZone";
import UploadInstructions from "./UploadInstructions";
import UploadValidationSummary from "./UploadValidationSummary";
import { useUploadCSV } from "../../api/ReportUploadApi";
import ReportBulkSelections from "./ReportBulkSelections";
import ErrorMessageContainer from "../../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../../shared-components/LoadingSpinner";
import { CheckIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import useNavigateNewPerson from "../../reports/useNavigateNewPerson";
import { useFamaFlags } from "../../api/FamaFlags";

type Props = {};

function buildErrors(data: {
  pulseChecked?: boolean;
  pulseVerified?: boolean;
  frequency?: number;
}) {
  let errors = { checkbox: false, frequency: false };
  if (data.pulseChecked && !data.pulseVerified) {
    errors.checkbox = true;
  } else {
    errors.checkbox = false;
  }

  if (data.pulseChecked && (data.frequency ?? 0) < 1) {
    errors.frequency = true;
  } else {
    errors.frequency = false;
  }

  return errors;
}

const ReportsBulkUpload = (props: Props) => {
  const { navigateCreatePerson } = useNavigateNewPerson();
  const navigate = useNavigate();
  const [isDirty, setIsDirty] = useState(false);

  const { v3UiBulkUpload2025 } = useFamaFlags();
  const bulkApiVersion = v3UiBulkUpload2025 ? 2 : 1;

  const [files, setFiles] = useState<File[]>();
  const {
    mutate: uploadCSV,
    error: errorUploading,
    isLoading: isUploading,
    isSuccess: isSuccessUpload,
  } = useUploadCSV();
  const [isReady, setIsReady] = useState(false);
  const [selection, setSelection] = useState<{
    companyUUID?: string;
    blueprintUUIDs: string[];
    behaviorKitUUID?: string;
    clientQa?: boolean;
    internalQa?: boolean;
    pulseChecked?: boolean;
    pulseVerified?: boolean;
    frequency?: number;
  }>({ blueprintUUIDs: [], pulseChecked: false });
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [hasSelectionErrors, setHasSelectionErrors] = useState<{
    checkbox: boolean;
    frequency: boolean;
  }>({ checkbox: false, frequency: false });

  function onReady() {
    if (!files || files.length === 0) return;
    setIsReady(true);
  }

  function onUpload() {
    setIsDirty(true);
    if (
      !files ||
      files.length === 0 ||
      !selection.companyUUID ||
      !selection.blueprintUUIDs
    ) {
      return;
    }

    let errors = buildErrors(selection);
    if (errors.checkbox || errors.frequency) {
      setHasSelectionErrors(errors);
      return;
    }

    uploadCSV({
      file: files[0],
      companyUUID: selection.companyUUID,
      blueprintUUIDs: selection.blueprintUUIDs,
      behaviorKitUUID: selection.behaviorKitUUID,
      clientQa: selection.clientQa,
      internalQa: selection.internalQa,
      pulseFrequency: selection.pulseChecked ? selection.frequency : undefined,
      version: bulkApiVersion,
    });

    navigate("/reports", {
      state: { reportSubmitted: true, reportsBulkUpload: true },
    });
  }

  function onSelectionChange(data: {
    companyUUID?: string;
    blueprintUUIDs: string[];
    behaviorKitUUID?: string;
    clientQa?: boolean;
    internalQa?: boolean;
    pulseChecked?: boolean;
    pulseVerified?: boolean;
    frequency?: number;
  }) {
    const errors = buildErrors(data);
    setHasSelectionErrors(errors);
    setSelection(data);
  }

  function onValidationComplete(hasErrors: boolean) {
    setHasValidationErrors(hasErrors);
  }

  const checkTypeReady =
    selection.companyUUID && selection.blueprintUUIDs.length > 0;

  const uploadDisabled =
    !isReady ||
    !checkTypeReady ||
    hasValidationErrors ||
    (isDirty && (hasSelectionErrors.checkbox || hasSelectionErrors.frequency));

  return (
    <div>
      <div>
        <Button
          text="Back"
          type={ButtonTypes.back}
          colorType={ButtonColorTypes.custom}
          className="w-20 h-20 max-w-xs max-h-9 px-0 items-start my-1"
          onClick={() => navigateCreatePerson()}
        />
      </div>
      <PageHeader title={"Reports"}></PageHeader>
      <Container className="flex justify-center items-center">
        {isSuccessUpload && (
          <div className="text-center text-gray-500">
            <div className="flex justify-center">
              <CheckIcon className="w-40 text-green-400" />
            </div>
            <p>Upload Successful</p>
            <p>Check your email for the report creation details.</p>
          </div>
        )}
        {!isSuccessUpload && (
          <div className="">
            <div>
              <ReportBulkSelections
                onChange={onSelectionChange}
                showErrors={isDirty ? hasSelectionErrors : undefined}
              />
              <UploadInstructions />
            </div>
            {!files && (
              <UploadDropZone
                files={files}
                onChange={(files) => setFiles(files)}
                isHidden={!checkTypeReady}
              />
            )}
            {files && (
              <UploadValidationSummary
                files={files}
                companyUUID={selection.companyUUID || ""}
                onReady={onReady}
                blueprintUUIDs={selection.blueprintUUIDs.join(",")}
                onValidationComplete={onValidationComplete}
                version={bulkApiVersion}
              />
            )}
            {errorUploading && (
              <ErrorMessageContainer>
                <>
                  Error with the bulk upload, please contact the administrator{" "}
                </>
              </ErrorMessageContainer>
            )}
            <div className="my-4 flex justify-end gap-4">
              {files && (
                <Button
                  text="Cancel"
                  colorType={ButtonColorTypes.cancel}
                  onClick={() => {
                    setFiles(undefined);
                    setIsReady(false);
                  }}
                />
              )}
              {isUploading && <LoadingSpinner />}
              {files && !isUploading && (
                <Button
                  text="Upload"
                  disabled={uploadDisabled}
                  onClick={onUpload}
                  className="w-52"
                />
              )}
            </div>
          </div>
        )}
      </Container>
    </div>
  );
};

export default ReportsBulkUpload;
